<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="property-type-filters-extension">
    <div
      v-if="showMobileTrigger"
      class="contained tw-w-full tw-bg-white tw-px-4 tw-pb-2 tw-pt-1 lg:tw-hidden"
    >
      <PropertyTypeFiltersTrigger
        :expanded="expanded"
        :disabled="disabled"
        class="tw-w-full"
        @toggle="$emit('update:expanded', !expanded)"
      />
    </div>

    <Transition
      name="property-type-filters-panel"
      @before-enter="beforeEnterPanel"
      @enter="enterPanel"
      @after-enter="afterEnterPanel"
      @before-leave="beforeLeavePanel"
      @leave="leavePanel"
      @after-leave="afterLeavePanel"
    >
      <div
        v-if="expanded"
        :class="[
          'tw-relative tw-border-t tw-border-b-gray-200 tw-bg-[#F9FAFB]',
          breakOutOfContainer
            ? 'panel-break-out tw-left-1/2 tw-w-screen tw-max-w-none -tw-translate-x-1/2'
            : 'tw-w-full',
        ]"
        data-testid="property-type-filters-panel"
      >
        <div class="tw-w-full tw-bg-[#F9FAFB]">
          <div
            class="contained tw-flex tw-w-full tw-flex-col tw-gap-[8px] tw-px-[8px] tw-py-[10px] lg:tw-p-[16px]"
          >
            <div
              class="lg:tw-mx-auto lg:tw-inline-flex lg:tw-max-w-full lg:tw-items-center lg:tw-gap-[24px]"
            >
              <div
                class="tw-hidden tw-shrink-0 lg:tw-flex lg:tw-items-center lg:tw-gap-[8px]"
              >
                <span class="tw-text-sm tw-font-semibold tw-text-[#3B4143]"
                  >Property types</span
                >
                <span class="tw-text-sm tw-font-normal tw-text-[#5C6A75]"
                  >Select one or more</span
                >
              </div>

              <div
                class="tw-flex tw-w-full tw-items-start tw-gap-[8px] tw-overflow-visible max-[375px]:tw-justify-center max-[375px]:tw-gap-[5px] lg:tw-w-auto lg:tw-flex-nowrap lg:tw-items-center lg:tw-gap-[24px] lg:tw-p-0"
              >
                <button
                  v-for="propertyType in visiblePropertyTypes"
                  :key="propertyType.id"
                  type="button"
                  :aria-pressed="isActiveType(propertyType.id)"
                  :aria-disabled="
                    isLastActiveType(propertyType.id) ? 'true' : 'false'
                  "
                  :data-testid="'inline-property-type-' + propertyType.id"
                  :disabled="disabled || isLastActiveType(propertyType.id)"
                  :class="[
                    'tw-flex tw-shrink-0 tw-items-center tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2',
                    isActiveType(propertyType.id)
                      ? 'tw-rounded-[16px] tw-border-2 tw-border-[#00628F] tw-bg-white tw-text-[#3B4143] lg:tw-rounded-[30px]'
                      : 'tw-border tw-border-[#D1D5DB] tw-text-[#3B4143] hover:tw-border-uhblue-700',
                    'tw-text-[16px] tw-font-[600] lg:tw-h-auto lg:tw-min-h-[0px] lg:tw-w-auto lg:tw-min-w-[0px] lg:tw-flex-row lg:tw-items-center lg:tw-gap-[8px] lg:tw-rounded-[30px] lg:tw-px-4 lg:tw-py-2 lg:tw-text-[16px]',
                    'tw-min-h-[75px] tw-w-[23.5%] tw-flex-col tw-justify-center tw-gap-[2px] tw-rounded-[16px] tw-px-1 tw-py-1 tw-text-[12px] max-[375px]:tw-w-[22.5%]',
                    disabled || isLastActiveType(propertyType.id)
                      ? 'tw-cursor-not-allowed'
                      : 'hover:tw-cursor-pointer',
                    isLastActiveType(propertyType.id) ? 'tw-opacity-80' : null,
                  ]"
                  @click="toggleType(propertyType.id)"
                >
                  <div
                    :class="[
                      'tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center lg:tw-h-4 lg:tw-w-4',
                      isActiveType(propertyType.id)
                        ? 'tw-text-[#00628F]'
                        : 'tw-text-[#3B4143]',
                    ]"
                    v-html="propertyType.icon"
                  ></div>
                  <span
                    class="tw-text-center tw-text-[12px] tw-font-normal tw-leading-tight lg:tw-text-[16px] lg:tw-font-[600] lg:tw-leading-5"
                  >
                    {{ propertyType.name }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import PropertyTypeFiltersTrigger from './PropertyTypeFiltersTrigger.vue';
import { PROPERTY_TYPES } from './constants.ts';
import {
  normalizePropertyTypeValue,
  propertyTypeSelectionToValue,
  togglePropertyTypeSelection,
} from './helpers.ts';
import { useSearchModeStore } from '@js/stores/search/searchModeStore';
import { sendLaunchDarklyMetric } from '@js/launchdarkly-track';

const PANEL_ANIMATION_MS = 300;

// Control points of the `ease` timing function the panel's transform and
// opacity still transition with, so the height keeps step with them.
const PANEL_EASE = { x1: 0.25, y1: 0.1, x2: 0.25, y2: 1 };

const bezierAt = (t, p1, p2) =>
  3 * (1 - t) * (1 - t) * t * p1 + 3 * (1 - t) * t * t * p2 + t * t * t;

const bezierSlopeAt = (t, p1, p2) =>
  3 * (1 - t) * (1 - t) * p1 +
  6 * (1 - t) * t * (p2 - p1) +
  3 * t * t * (1 - p2);

const easedProgress = (fraction) => {
  let t = fraction;

  for (let i = 0; i < 5; i += 1) {
    const slope = bezierSlopeAt(t, PANEL_EASE.x1, PANEL_EASE.x2);

    if (Math.abs(slope) < 1e-6) {
      break;
    }

    t -= (bezierAt(t, PANEL_EASE.x1, PANEL_EASE.x2) - fraction) / slope;
  }

  return bezierAt(Math.min(Math.max(t, 0), 1), PANEL_EASE.y1, PANEL_EASE.y2);
};

export default {
  name: 'PropertyTypeFiltersExtension',
  components: {
    PropertyTypeFiltersTrigger,
  },
  props: {
    active: {
      type: [String, Array],
      default: null,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    enablePrivateHallsFilter: {
      type: Boolean,
      default: false,
    },
    enableSpareRoomFunctionality: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showMobileTrigger: {
      type: Boolean,
      default: true,
    },
    breakOutOfContainer: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['change', 'update:expanded'],
  data() {
    return {
      activeTypes: [],
      collapsedByDisabledState: false,
      syncingFromProp: false,
    };
  },
  computed: {
    blendedSearch() {
      return useSearchModeStore().blendedSearch;
    },
    propertyTypes() {
      return PROPERTY_TYPES.map((type) => {
        let visible = type.visible;

        if (type.id === 'private-halls') {
          visible = this.enablePrivateHallsFilter;
        } else if (type.id === 'spare-rooms') {
          visible = this.enableSpareRoomFunctionality;
        }

        return {
          ...type,
          visible,
        };
      });
    },
    visiblePropertyTypes() {
      return this.propertyTypes.filter((type) => type.visible);
    },
  },
  watch: {
    active: {
      deep: true,
      immediate: true,
      handler() {
        this.syncActiveTypesFromProp();
      },
    },
    disabled: {
      immediate: true,
      handler(isDisabled) {
        if (isDisabled && this.expanded) {
          this.collapsedByDisabledState = true;
          this.$emit('update:expanded', false);
          return;
        }

        if (!isDisabled && this.collapsedByDisabledState && !this.expanded) {
          this.collapsedByDisabledState = false;
          this.$emit('update:expanded', true);
        }
      },
    },
    activeTypes: {
      deep: true,
      handler() {
        if (!this.syncingFromProp) {
          this.emitChange();
        }
      },
    },
  },
  created() {
    this.panelHeightFrame = null;
  },
  mounted() {
    this.syncActiveTypesFromProp();
  },
  beforeDestroy() {
    this.cancelPanelHeightAnimation();
  },
  methods: {
    isActiveType(typeId) {
      return this.activeTypes.includes(typeId);
    },
    isLastActiveType(typeId) {
      return this.activeTypes.length === 1 && this.activeTypes[0] === typeId;
    },
    toggleType(typeId) {
      if (this.disabled || this.isLastActiveType(typeId)) {
        return;
      }

      const previousTypes = [...this.activeTypes];
      this.activeTypes = togglePropertyTypeSelection(
        this.activeTypes,
        typeId,
        this.blendedSearch
      );

      if (
        previousTypes.length === this.activeTypes.length &&
        previousTypes.every((type, index) => type === this.activeTypes[index])
      ) {
        return;
      }

      this.trackPropertyTypeFilterApplied(typeId);
    },
    activeTypeIdsEqual(nextActiveTypes) {
      if (this.activeTypes.length !== nextActiveTypes.length) {
        return false;
      }

      return this.activeTypes.every(
        (type, index) => type === nextActiveTypes[index]
      );
    },
    syncActiveTypesFromProp() {
      const activeIds = normalizePropertyTypeValue(this.active).filter((id) =>
        this.propertyTypes.some((type) => type.id === id && type.visible)
      );

      if (this.activeTypeIdsEqual(activeIds)) {
        return;
      }

      this.syncingFromProp = true;
      this.activeTypes = activeIds;
      this.syncingFromProp = false;
    },
    emitChange() {
      const value = propertyTypeSelectionToValue(this.activeTypes, true);
      this.$emit('change', value);
    },
    trackPropertyTypeFilterApplied(typeId) {
      sendLaunchDarklyMetric(
        'property_type_filters_applied',
        {
          type: typeId,
          test_name: 'property_type_filters_test',
        },
        { transport: 'axios' }
      );
    },
    // The height is stepped here rather than transitioned in CSS because CSS
    // interpolates it in fractional pixels. Scroll anchoring then corrects the
    // scroll offset by a whole pixel every frame, and the leftover fraction
    // flips sign frame to frame, which reads as a shimmer in everything below
    // the panel. Whole-pixel heights make the anchoring correction exact.
    animatePanelHeight(el, from, to) {
      this.cancelPanelHeightAnimation();

      const startedAt = performance.now();

      const step = (now) => {
        const fraction = Math.min((now - startedAt) / PANEL_ANIMATION_MS, 1);

        el.style.height = `${Math.round(
          from + (to - from) * easedProgress(fraction)
        )}px`;

        this.panelHeightFrame =
          fraction < 1 ? requestAnimationFrame(step) : null;
      };

      this.panelHeightFrame = requestAnimationFrame(step);
    },
    cancelPanelHeightAnimation() {
      if (this.panelHeightFrame) {
        cancelAnimationFrame(this.panelHeightFrame);
        this.panelHeightFrame = null;
      }
    },
    resetPanelHeight(el) {
      this.cancelPanelHeightAnimation();
      el.style.height = '';
      el.style.overflow = '';
    },
    beforeEnterPanel(el) {
      el.style.overflow = 'hidden';
      el.style.height = '0px';
    },
    enterPanel(el) {
      this.animatePanelHeight(el, 0, el.scrollHeight);
    },
    afterEnterPanel(el) {
      this.resetPanelHeight(el);
    },
    beforeLeavePanel(el) {
      el.style.overflow = 'hidden';
      el.style.height = `${el.scrollHeight}px`;
    },
    leavePanel(el) {
      // Force reflow so the browser applies the starting height before collapsing.
      void el.offsetHeight;
      this.animatePanelHeight(el, el.scrollHeight, 0);
    },
    afterLeavePanel(el) {
      this.resetPanelHeight(el);
    },
  },
};
</script>

<style scoped>
.property-type-filters-panel-enter-active,
.property-type-filters-panel-leave-active {
  overflow: hidden;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.property-type-filters-panel-enter,
.property-type-filters-panel-leave-to {
  transform: translateX(var(--panel-bleed-x, 0)) translateY(-12px);
  opacity: 0;
}

.panel-break-out {
  --panel-bleed-x: -50%;
}
</style>
